import { Box, Divider } from "@mui/material";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { TGDropDownItem } from "../../../../components/common/DropDowns/TGDropDown/TGDropDown.tsx";
import TextToImageMediumsDropdown from "../../../../components/common/DropDowns/TextToImageMediumsDropdown.tsx";
import TextToImageModelsDropdown, {
	getModelKey,
	getModelName,
	getModelVersion,
} from "../../../../components/common/DropDowns/TextToImageModelsDropdown/NewTextToImageModelsDropdown.tsx";
import { APPS } from "../../../../constants/AppsConstants.ts";
import LinkConstants from "../../../../constants/LinkConstants.ts";
import { IMAGE_REFERENCE_CONFIGS } from "../../../../constants/TextToImageConstants.ts";
import { useAppStore } from "../../../../hooks/useStores.tsx";
import InputLayout from "../../../../layout/InputLayout/InputLayout.tsx";
import { modelTypes } from "../../../../models/text-to-image.ts";
import { isTextToVector } from "../../../../utils/index.ts";
import iframeStore from "../../../IframeNew/iframe-store.tsx";
import TgInfluenceSlider from "../../../NewTG/App/Image/Config/Influence/TgInfluenceSlider.tsx";
import TextToImageAspectRatio from "./Advanced/AspectRatio/TextToImageAspectRatio.tsx";
import TextToImageAdvanced from "./Advanced/TextToImageAdvanced.tsx";
import ImageReference from "./ImageReference/ImageReference.tsx";
import styles from "./TextToImageConfig.module.scss";

const TextToImageConfig = () => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.textToImage.config" });
	const { textToImageStore, playgroundStore } = useAppStore();
	const { config, handleConfigChange } = textToImageStore;
	const containerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		runInAction(() => {
			playgroundStore.handleAppChange(APPS.TEXT_TO_IMAGE);
			textToImageStore.errors = {};
			textToImageStore.IpSignalWarning = false;
		});
	}, []);

	useEffect(() => {
		textToImageStore.imageGenerationContainerRef = containerRef as any;
	}, [containerRef]);

	useEffect(() => {
		playgroundStore.handleAppChange(APPS.TEXT_TO_IMAGE);
		if (isTextToVector()) {
			handleConfigChange("prompt_enhancement", false);
			handleConfigChange("format", "svg");
		} else {
			handleConfigChange("prompt_enhancement", true);
			handleConfigChange("format", "png");
		}
	}, [iframeStore.iframe?.config?.landing_page]);

	const shouldHideReimagine =
		config?.model === "fibo" || (!!config?.tailored_model_id && config?.training_version === "fibo");

	const clearFoundationModelsSelections = () => {
		runInAction(() => {
			textToImageStore.config = {
				...textToImageStore.config,
				model: "",
				model_version: "",
				medium: "",
			};
		});
	};

	const clearTailoredModelsSelections = () => {
		runInAction(() => {
			textToImageStore.config = {
				...textToImageStore.config,
				tailored_model_id: undefined,
				training_version: undefined,
				generation_prefix: undefined,
				include_generation_prefix: undefined,
				negative_prompt: "",
			};
		});
	};

	const handleFoundationModelsChange = (selectedOption: TGDropDownItem) => {
		handleConfigChange("model_name", selectedOption.key);
		clearTailoredModelsSelections();

		if (selectedOption?.id === "tailored_models") {
			clearFoundationModelsSelections();
			handleConfigChange("model_id", "tailored_models");
			return;
		}

		runInAction(() => {
			textToImageStore.config = {
				...textToImageStore.config,
				content_moderation:
					selectedOption?.id === "fibo" || selectedOption?.id === "fibo_lite"
						? true
						: textToImageStore.config.content_moderation,
				model: getModelName(selectedOption.value as string),
				model_id: selectedOption?.id,
				model_version: getModelVersion(selectedOption.value as string),
				fast: getModelName(selectedOption.value as string) === modelTypes.Fast,
			};
			const defaultSteps =
				IMAGE_REFERENCE_CONFIGS.steps[
					textToImageStore.config?.model_id === "fibo"
						? "fibo"
						: textToImageStore.config?.model_id === "fibo_lite"
							? "fibo_lite"
							: textToImageStore.config?.fast
								? "fast"
								: "not_fast"
				];
			textToImageStore.handleConfigChange("steps_num", defaultSteps.default);
		});
	};

	const handleTailoredModelsChange = (selectedOption: TGDropDownItem) => {
		clearFoundationModelsSelections();
		const { key, value, extraData } = selectedOption;
		const { training_version, generation_prefix, active_model_version } = extraData || {};

		runInAction(() => {
			textToImageStore.config = {
				...textToImageStore.config,
				model: "",
				model_version: "",
				model_name: key,
				model_id: value,
				tailored_model_id: value,
				checkpoint_number: active_model_version,
				training_version,
				generation_prefix,
				include_generation_prefix: false,
				negative_prompt: undefined,
				content_moderation: false,
				prompt_enhancement: false,
			};
		});
	};

	return (
		<>
			<Box className={styles.config} ref={containerRef}>
				<InputLayout
					label={t("models.label")}
					labelSize="large"
					docsLink={
						isTextToVector() ? LinkConstants.BRIA_API_TEXT_TO_VECTOR : LinkConstants.BRIA_API_TEXT_TO_IMAGE
					}
				>
					<TextToImageModelsDropdown
						value={config.model_id || getModelKey(config.model, config.model_version)}
						onFoundationModelsChange={handleFoundationModelsChange}
						onTailoredModelsChange={handleTailoredModelsChange}
						placeholder={t("models.placeholder")}
					/>
				</InputLayout>
				{textToImageStore.config.tailored_model_id && (
					<>
						<TgInfluenceSlider />
					</>
				)}

				<TextToImageAspectRatio />
				{!isTextToVector() &&
					!config.tailored_model_id &&
					!(config?.training_version == "fibo" || config?.model == "fibo") && (
						<InputLayout label={t("medium.label")} labelClassName={styles.inputLabel} labelSize="large">
							<TextToImageMediumsDropdown
								value={config?.medium}
								onChange={(e) => {
									handleConfigChange("medium", e.target.value as string);
								}}
								defaultOption
								placeholder={
									textToImageStore.isImageReferenceEnabled() &&
									textToImageStore.config.model !== "fibo"
										? t("medium.disabledPlaceholder")
										: t("medium.placeholder")
								}
								disabled={
									textToImageStore.isImageReferenceEnabled() &&
									textToImageStore.config.model !== "fibo"
								}
							/>
						</InputLayout>
					)}
				<Divider className={styles.divider} />
				{(!iframeStore.isIframe() ||
					iframeStore.iframe.config.image_generation_config?.enable_structure_guidance) &&
					!shouldHideReimagine && (
						<>
							<ImageReference />
							<Divider className={styles.divider} />
						</>
					)}

				<TextToImageAdvanced />
			</Box>
		</>
	);
};

export default observer(TextToImageConfig);
